import React, { useState, useEffect, useRef } from 'react';
import {
  QrCode,
  Plus,
  Layers,
  Trash2,
  Eye,
  ToggleLeft,
  ToggleRight,
  Copy,
  MoreVertical,
  Printer,
  Building2,
  ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useQRCodes from '../../hooks/useQRCodes';
import GenerateQRDialog from './GenerateQRDialog';
import ViewQRDialog from './ViewQRDialog';
import ConfirmDialog from './ConfirmDialog';
import PrintQRDialog from './PrintQRDialog';
import { useAuth } from '../../context/AuthContext';
import branchService from '../../services/branchService';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

export default function QRCodeManagement() {
  const { user } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  // Load branches for owner
  useEffect(() => {
    if (isOwner) {
      branchService.getAllBranches().then(res => {
        const list = res?.data || res || [];
        setBranches(list);
      }).catch(() => {});
    }
  }, [isOwner]);

  const {
    qrCodes,
    loading,
    filter,
    setFilter,
    createQRCode,
    bulkCreate,
    toggleQRCode,
    deleteQRCode,
    deleteMultiple,
    deleteAll,
    availableTables,
  } = useQRCodes(selectedBranchId || undefined);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [viewCode, setViewCode] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);

  // New enterprise state
  const [selectedIds, setSelectedIds] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, openUp: false });
  const [printDialogCodes, setPrintDialogCodes] = useState(null);
  const [printDialogTitle, setPrintDialogTitle] = useState('');
  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);
  const menuRef = useRef(null);

  // Outside-click handler for action menus
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (!e.target.closest('.action-menu-trigger')) setOpenActionMenu(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Reposition on scroll/resize while menu is open
  useEffect(() => {
    if (!openActionMenu) return;
    const close = () => setOpenActionMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openActionMenu]);

  const openMenu = (e, codeId) => {
    e.stopPropagation();
    if (openActionMenu === codeId) {
      setOpenActionMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuH = 260; // approximate dropdown height
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuH;
    setMenuPos({
      top: openUp ? rect.top : rect.bottom + 4,
      left: rect.right,
      openUp,
    });
    setOpenActionMenu(codeId);
  };

  // Clean up selection when data changes
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => qrCodes.some((q) => q.id === id)));
  }, [qrCodes]);

  const allSelected = qrCodes.length > 0 && selectedIds.length === qrCodes.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < qrCodes.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(qrCodes.map((q) => q.id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success('URL copied'));
  };

  const openExportDialog = (codes, dialogTitle) => {
    setPrintDialogCodes(codes);
    setPrintDialogTitle(dialogTitle);
  };

  const handleExportAll = () => openExportDialog(qrCodes, 'Export All QR Codes');

  const handleExportSelected = () => {
    const selected = qrCodes.filter((q) => selectedIds.includes(q.id));
    openExportDialog(selected, 'Export Selected QR Codes');
  };

  const handleExportSingle = (code) => {
    openExportDialog([code], `Export QR — ${code.tableName}`);
    setOpenActionMenu(null);
  };

  const handleDeleteSelected = async () => {
    await deleteMultiple(selectedIds);
    setSelectedIds([]);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-8 w-8 bg-slate-200 rounded-lg animate-pulse" />
          <div className="h-6 w-56 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4 py-3">
              <div className="h-4 w-8 bg-slate-100 rounded animate-pulse" />
              <div className="h-4 w-24 bg-slate-100 rounded animate-pulse" />
              <div className="h-4 w-20 bg-slate-100 rounded animate-pulse" />
              <div className="h-4 flex-1 bg-slate-100 rounded animate-pulse" />
              <div className="h-5 w-14 bg-slate-100 rounded-full animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <QrCode size={20} className="text-purple-600" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">QR Code Management</h1>
          <p className="text-xs text-slate-500">Generate and manage QR codes for your tables</p>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setGenerateOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
        >
          <Plus size={14} />
          Generate QR for Table
        </button>
        <button
          onClick={() => setConfirmBulk(true)}
          disabled={availableTables.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Layers size={14} />
          Generate All QR Codes
        </button>
        {qrCodes.length > 0 && (
          <>
            <button
              onClick={handleExportAll}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Printer size={14} />
              Print / Download
            </button>
            <button
              onClick={() => setConfirmDeleteAll(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            >
              <Trash2 size={14} />
              Delete All
            </button>
          </>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Branch Dropdown */}
        {isOwner && branches.length > 1 && (
          <div className="relative">
            <Building2 size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
              className="appearance-none pl-8 pr-7 py-2 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.branchName || b.name}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        )}

        {/* Filter */}
        <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === f.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Toolbar */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-4 py-2.5 bg-purple-50 border border-purple-200 rounded-lg">
          <span className="text-xs font-medium text-purple-700">
            {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setSelectedIds([])}
            className="text-xs text-purple-600 hover:text-purple-800 underline underline-offset-2 transition-colors"
          >
            Clear
          </button>
          <div className="flex-1" />
          <button
            onClick={handleExportSelected}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-300 bg-white text-purple-700 hover:bg-purple-100 transition-colors"
          >
            <Printer size={13} />
            Print / Download
          </button>
          <button
            onClick={() => setConfirmDeleteSelected(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            <Trash2 size={13} />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table or Empty State */}
      {qrCodes.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
            <QrCode size={24} className="text-purple-500" />
          </div>
          <h3 className="text-sm font-semibold text-slate-900 mb-1">No QR codes generated yet</h3>
          <p className="text-xs text-slate-500 mb-4">Generate QR codes for your tables so customers can order from their phones</p>
          <button
            onClick={() => setGenerateOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
          >
            <Plus size={14} />
            Generate Your First QR Code
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Table</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Floor</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">QR URL</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {qrCodes.map((code, index) => {
                  const isSelected = selectedIds.includes(code.id);
                  return (
                    <tr
                      key={code.id}
                      className={`border-b border-slate-50 transition-colors ${
                        isSelected ? 'bg-purple-50/60' : 'hover:bg-slate-50/50'
                      }`}
                    >
                      <td className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectOne(code.id)}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{index + 1}</td>
                      <td className="px-4 py-3 text-xs font-medium text-slate-900">{code.tableName}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{code.floorName}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-500 font-mono break-all">{code.qrUrl}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            code.isActive
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200'
                          }`}
                        >
                          {code.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={(e) => openMenu(e, code.id)}
                            className="action-menu-trigger p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <MoreVertical size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <p className="text-[11px] text-slate-400">
              {qrCodes.length} QR code{qrCodes.length !== 1 ? 's' : ''} total
              {availableTables.length > 0 && ` · ${availableTables.length} table${availableTables.length !== 1 ? 's' : ''} without QR codes`}
            </p>
          </div>
        </div>
      )}

      {/* Context Menu — fixed position, rendered outside table */}
      {openActionMenu && (() => {
        const code = qrCodes.find((q) => q.id === openActionMenu);
        if (!code) return null;
        return (
          <div
            ref={menuRef}
            className="fixed w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1"
            style={{
              top: menuPos.openUp ? undefined : menuPos.top,
              bottom: menuPos.openUp ? window.innerHeight - menuPos.top : undefined,
              left: menuPos.left - 192, // w-48 = 12rem = 192px, align right edge to button
            }}
          >
            <button
              onClick={() => { setViewCode(code); setOpenActionMenu(null); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Eye size={14} className="text-slate-400" />
              View QR
            </button>
            <button
              onClick={() => handleExportSingle(code)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Printer size={14} className="text-slate-400" />
              Print / Download
            </button>
            <button
              onClick={() => { copyUrl(code.qrUrl); setOpenActionMenu(null); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Copy size={14} className="text-slate-400" />
              Copy URL
            </button>
            <button
              onClick={() => { toggleQRCode(code.id); setOpenActionMenu(null); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {code.isActive ? (
                <ToggleRight size={14} className="text-emerald-500" />
              ) : (
                <ToggleLeft size={14} className="text-slate-400" />
              )}
              {code.isActive ? 'Deactivate' : 'Activate'}
            </button>
            <div className="my-1 border-t border-slate-100" />
            <button
              onClick={() => { setConfirmDelete(code); setOpenActionMenu(null); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        );
      })()}

      {/* Dialogs */}
      <GenerateQRDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        availableTables={availableTables}
        onGenerate={createQRCode}
      />

      <ViewQRDialog
        open={!!viewCode}
        onClose={() => setViewCode(null)}
        qrCode={viewCode}
      />

      <PrintQRDialog
        open={!!printDialogCodes}
        onClose={() => setPrintDialogCodes(null)}
        qrCodes={printDialogCodes || []}
        title={printDialogTitle}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteQRCode(confirmDelete?.id)}
        title="Delete QR Code"
        message={`Are you sure you want to delete the QR code for ${confirmDelete?.tableName}? This action cannot be undone.`}
        confirmLabel="Delete"
        danger
      />

      <ConfirmDialog
        open={confirmDeleteSelected}
        onClose={() => setConfirmDeleteSelected(false)}
        onConfirm={handleDeleteSelected}
        title="Delete Selected QR Codes"
        message={`Are you sure you want to delete ${selectedIds.length} selected QR code${selectedIds.length !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmLabel="Delete Selected"
        danger
      />

      <ConfirmDialog
        open={confirmDeleteAll}
        onClose={() => setConfirmDeleteAll(false)}
        onConfirm={deleteAll}
        title="Delete All QR Codes"
        message="Are you sure you want to delete all QR codes? This will remove QR codes from every table. This action cannot be undone."
        confirmLabel="Delete All"
        danger
      />

      <ConfirmDialog
        open={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        onConfirm={bulkCreate}
        title="Generate All QR Codes"
        message={`This will generate QR codes for ${availableTables.length} table${availableTables.length !== 1 ? 's' : ''} that don't have one yet. Continue?`}
        confirmLabel="Generate All"
      />
    </div>
  );
}
